import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Tabs, Tag, Tooltip, Spin } from "antd";
import { Container } from "./style";
import CreateContentModal from "./CreateContentModal";
import PracticeManager from "./PracticeManager";
import QuoteManager from "./QuoteManager";
import PromptManager from "./PromptManager";
import ChallengeManager from "./ChallengeManager";
import ActionManager from "./ActionManager";
import { setPracticeData } from "../../../store/features/CreatePractice";
import { setQuoteData } from "../../../store/features/CreateQuote";
import { setPromptData } from "../../../store/features/CreatePrompt";
import { setChallengeData } from "../../../store/features/CreateChallenge";
import { setActionData } from "../../../store/features/CreateAction";
import { gql, useLazyQuery } from "@apollo/client";

// TODO: add actions count
const GET_COUNT = gql`
  query GetCountsOfMyContentAndSubscriptions {
    getCountsOfMyContentAndSubscriptions {
      practices
      quotes
      prompts
      subscriptions
      challenges
      actions
    }
  }
`;

const CONTENT_DESCRIPTIONS = {
  challenge:
    "Create a multi-day journey with daily Reflection, Insight, and Action. Users engage by completing each step and tracking progress. Practitioners can use AI to quickly generate a draft.",
  reflection:
    "Send a journaling question that invites users to pause and share through comments, replies, or emojis.",
  insight:
    "Share a quick idea with an external link to learn more. Users can read and tap through to explore.",
  action:
    "Prompt users to step away and do something in real life. Users engage by reacting with emojis once done.",
  practice:
    "Guide users through a self-paced, micro-coaching like exercise. Users engage by completing steps, reflecting, and checking in on their own.",
};

const renderTabLabel = (title, count, description) => (
  <Tooltip
    title={description}
    placement="bottom"
    overlayInnerStyle={{ maxWidth: 320 }}
  >
    <span>
      {title}
      <Tag className="ml-10">{count}</Tag>
    </span>
  </Tooltip>
);

const GROUPS_BY_COACH = gql`
  query GroupsByCoach($coachId: String!) {
    groupsByCoach(coachId: $coachId) {
      _id
      name
      memberCount
    }
  }
`;

const COACH_DASHBOARD_KPIS = gql`
  query CoachDashboardKpis($coachId: String!, $timezone: String) {
    coachDashboardKpis(coachId: $coachId, timezone: $timezone) {
      totalSubscribers
      activeThisMonth
      totalMonthlyActivities
      windowStart
      windowEnd
    }
  }
`;

const KPI_ITEMS = [
  { key: "totalSubscribers", label: "Total Subscribers", clickable: true },
  { key: "activeThisMonth", label: "Active this month" },
  { key: "totalMonthlyActivities", label: "Total Monthly Activities" },
];

export default function ContentManager() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [params] = useSearchParams();
  const [groups, setGroups] = useState([]);

  const [getCount, { data: countRes, refetch }] = useLazyQuery(GET_COUNT);
  const [fetchDashboardKpis, { data: kpiData, loading: kpiLoading }] =
    useLazyQuery(COACH_DASHBOARD_KPIS, { fetchPolicy: "network-only" });
  const [practiceCount, setPracticeCount] = useState(0);
  const [quoteCount, setQuoteCount] = useState(0);
  const [promptCount, setPromptCount] = useState(0);
  const [challengeCount, setChallengeCount] = useState(0);
  const [actionCount, setActionCount] = useState(0);

  const [fetchGroups] = useLazyQuery(GROUPS_BY_COACH, {
    variables: { coachId: userInfo?.coach?._id || null },
    onCompleted: (data) => {
      if (data?.groupsByCoach) {
        setGroups(data.groupsByCoach);
      }
    },
    fetchPolicy: "network-only",
  });
  useEffect(() => {
    refetch();
    dispatch(setPracticeData({}));
    dispatch(setQuoteData({}));
    dispatch(setPromptData({}));
    dispatch(setChallengeData({}));
    dispatch(setActionData({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch groups on mount
  useEffect(() => {
    if (userInfo?.coach?._id) {
      fetchGroups();
      fetchDashboardKpis({
        variables: {
          coachId: userInfo.coach._id,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.coach?._id]);
  useEffect(() => {
    setPracticeCount(countRes?.getCountsOfMyContentAndSubscriptions?.practices);
    setQuoteCount(countRes?.getCountsOfMyContentAndSubscriptions?.quotes);
    setPromptCount(countRes?.getCountsOfMyContentAndSubscriptions?.prompts);
    setChallengeCount(
      countRes?.getCountsOfMyContentAndSubscriptions?.challenges
    );
    setActionCount(countRes?.getCountsOfMyContentAndSubscriptions?.actions);
  }, [countRes]);

  const tabName = params.get("tabName") || "4";
  const [activeTabKey, setActiveTabKey] = useState(tabName);

  useEffect(() => {
    setActiveTabKey(params.get("tabName") || "4");
  }, [params]);

  const handleTabChange = (key) => {
    setActiveTabKey(key);
  };

  const tabs = [
    {
      key: "4",
      label: renderTabLabel(
        "Challenges",
        challengeCount,
        CONTENT_DESCRIPTIONS.challenge
      ),
      children: <ChallengeManager onDeleteSuccess={refetch} />,
    },
    {
      key: "3",
      label: renderTabLabel(
        "Reflections",
        promptCount,
        CONTENT_DESCRIPTIONS.reflection
      ),
      children: <PromptManager onDeleteSuccess={refetch} groups={groups} />,
    },
    {
      key: "2",
      label: renderTabLabel(
        "Insights",
        quoteCount,
        CONTENT_DESCRIPTIONS.insight
      ),
      children: <QuoteManager onDeleteSuccess={refetch} groups={groups} />,
    },
    {
      key: "5",
      label: renderTabLabel(
        "Actions",
        actionCount,
        CONTENT_DESCRIPTIONS.action
      ),
      children: <ActionManager onDeleteSuccess={refetch} groups={groups} />,
    },
    {
      key: "1",
      label: renderTabLabel(
        "Practices",
        practiceCount,
        CONTENT_DESCRIPTIONS.practice
      ),
      children: <PracticeManager onDeleteSuccess={refetch} groups={groups} />,
    },
  ];

  useEffect(() => {
    getCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dashboardKpis = kpiData?.coachDashboardKpis;

  // Actions
  const onAdd = () => {
    setIsCreateModalOpen(true);
  };

  return (
    <Container>
      <CreateContentModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        navigate={navigate}
      />
      <div className="content-manager-header">
        <div className="flex-between">
          <div className="page-title">Content Manager</div>
          <div className="dashboard-kpis">
            {KPI_ITEMS.map(({ key, label, clickable }) => (
              <div
                key={key}
                className={`dashboard-kpi-card${clickable ? " dashboard-kpi-card--clickable" : ""}`}
                onClick={
                  clickable ? () => navigate("/SubscriberManager") : undefined
                }
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={
                  clickable
                    ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate("/SubscriberManager");
                      }
                    }
                    : undefined
                }
              >
                <div className="dashboard-kpi-value">
                  {kpiLoading ? (
                    <Spin size="small" />
                  ) : (
                    dashboardKpis?.[key] ?? 0
                  )}
                </div>
                <div className="dashboard-kpi-label">{label}</div>
              </div>
            ))}
          </div>
          <div>
            <Button size="large" type="primary" onClick={onAdd}>
              Create new content
            </Button>
          </div>
        </div>
      </div>
      <Tabs
        activeKey={activeTabKey}
        onChange={handleTabChange}
        items={tabs}
        className="content-tabs"
      />
    </Container>
  );
}
